(() => {
  const STORAGE_KEY = 'solvi:saved-products';

  const readSavedProducts = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return new Set(Array.isArray(value) ? value.map(String) : []);
    } catch (_error) {
      return new Set();
    }
  };

  const writeSavedProducts = (saved) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(saved)));
    } catch (_error) {
      // Saving is optional; the rest of the collection remains fully usable.
    }
  };

  const toggleBodyLock = () => {
    const hasOpenDialog = Boolean(document.querySelector('[data-solvi-filter-dialog][open], [data-solvi-quick-dialog][open]'));
    document.body.classList.toggle('solvi-dialog-open', hasOpenDialog);
  };

  const openDialog = (dialog, trigger) => {
    if (!dialog) return;
    dialog._solviTrigger = trigger;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    toggleBodyLock();
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.hasAttribute('open')) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    toggleBodyLock();
  };

  const setupDialog = (dialog) => {
    if (!dialog || dialog.dataset.solviReady === 'true') return;
    dialog.dataset.solviReady = 'true';

    dialog.querySelectorAll('[data-solvi-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog(dialog));
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      toggleBodyLock();
      const trigger = dialog._solviTrigger;
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      dialog._solviTrigger = null;
    });

    dialog.addEventListener('cancel', () => window.setTimeout(toggleBodyLock));
  };

  const setupFavorites = (root) => {
    if (window.SolviScentShelf?.initialize) {
      window.SolviScentShelf.initialize(root);
      return;
    }

    const saved = readSavedProducts();

    root.querySelectorAll('[data-solvi-save]').forEach((button) => {
      if (button.dataset.solviReady === 'true') return;
      button.dataset.solviReady = 'true';

      const card = button.closest('[data-solvi-product-card]');
      const productId = String(card?.dataset.productId || '');
      if (!productId) return;

      const update = () => {
        const isSaved = saved.has(productId);
        button.setAttribute('aria-pressed', String(isSaved));
        const productName = button.getAttribute('aria-label')?.replace(/^(Save|Remove)\s+/, '') || 'product';
        button.setAttribute('aria-label', `${isSaved ? 'Remove' : 'Save'} ${productName}`);
      };

      update();
      button.addEventListener('click', () => {
        if (saved.has(productId)) saved.delete(productId);
        else saved.add(productId);
        writeSavedProducts(saved);
        update();
      });
    });
  };

  const setupFamilySelectors = (root) => {
    const dropdowns = Array.from(root.querySelectorAll('[data-solvi-family-dropdown]'));

    dropdowns.forEach((dropdown) => {
      if (dropdown.dataset.solviReady === 'true') return;
      dropdown.dataset.solviReady = 'true';

      const plate = dropdown.closest('.solvi-family-plate');
      const select = plate?.querySelector('[data-solvi-family-select]');
      const summary = dropdown.querySelector('summary');
      const visibleValue = dropdown.querySelector('[data-solvi-plate-value]');
      const options = Array.from(dropdown.querySelectorAll('[data-solvi-family-option]'));
      const form = dropdown.closest('[data-solvi-family-form]');

      if (!plate || !select || !summary || !visibleValue || !options.length || !form) return;

      const syncFromSelect = () => {
        const selectedOption = select.options[select.selectedIndex];
        const selectedValue = select.value || '';

        if (selectedOption) {
          visibleValue.textContent = selectedOption.textContent.trim();
        }

        options.forEach((option) => {
          option.setAttribute(
            'aria-selected',
            String((option.dataset.value || '') === selectedValue)
          );
        });

        summary.setAttribute('aria-expanded', String(dropdown.open));
      };

      const close = ({ restoreFocus = false } = {}) => {
        dropdown.open = false;
        summary.setAttribute('aria-expanded', 'false');
        if (restoreFocus) summary.focus({ preventScroll: true });
      };

      if (select.dataset.solviNativeReady !== 'true') {
        select.dataset.solviNativeReady = 'true';

        select.addEventListener('change', () => {
          syncFromSelect();
          form.requestSubmit();
        });
      }

      options.forEach((option, index) => {
        option.addEventListener('click', () => {
          if (option.disabled) return;

          select.value = option.dataset.value || '';
          syncFromSelect();
          close();

          select.dispatchEvent(new Event('change', { bubbles: true }));
        });

        option.addEventListener('keydown', (event) => {
          let nextIndex = null;

          if (event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = options.length - 1;

          if (event.key === 'Escape') {
            event.preventDefault();
            close({ restoreFocus: true });
            return;
          }

          if (nextIndex === null) return;

          event.preventDefault();

          let candidate = options[nextIndex];
          let guard = 0;

          while (candidate?.disabled && guard < options.length) {
            nextIndex = event.key === 'ArrowUp'
              ? (nextIndex - 1 + options.length) % options.length
              : (nextIndex + 1) % options.length;
            candidate = options[nextIndex];
            guard += 1;
          }

          candidate?.focus();
        });
      });

      dropdown.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(dropdown.open));

        if (!dropdown.open) return;

        // Only one family menu should be open at once.
        dropdowns.forEach((otherDropdown) => {
          if (otherDropdown !== dropdown) otherDropdown.open = false;
        });
      });

      summary.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close({ restoreFocus: true });
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        event.preventDefault();
        dropdown.open = true;

        const enabledOptions = options.filter((option) => !option.disabled);
        const selected = enabledOptions.find(
          (option) => option.getAttribute('aria-selected') === 'true'
        );

        window.requestAnimationFrame(() => {
          (selected || enabledOptions[0])?.focus();
        });
      });

      syncFromSelect();
    });

    if (root.dataset.solviFamilyOutsideReady !== 'true') {
      root.dataset.solviFamilyOutsideReady = 'true';

      document.addEventListener('click', (event) => {
        dropdowns.forEach((dropdown) => {
          if (dropdown.open && !dropdown.contains(event.target)) {
            dropdown.open = false;
            dropdown.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }
  };

  const setupSortDropdowns = (root) => {
    root.querySelectorAll('[data-solvi-sort-dropdown]').forEach((dropdown) => {
      if (dropdown.dataset.solviReady === 'true') return;
      dropdown.dataset.solviReady = 'true';

      const details = dropdown.querySelector('details');
      const summary = dropdown.querySelector('[data-solvi-sort-summary]');
      const input = dropdown.querySelector('[data-solvi-sort-input]');
      const value = dropdown.querySelector('[data-solvi-sort-value]');
      const options = Array.from(dropdown.querySelectorAll('[data-solvi-sort-option]'));
      if (!details || !summary || !input || !value || !options.length) return;

      const setExpanded = () => summary.setAttribute('aria-expanded', String(details.open));
      const close = ({ restoreFocus = false } = {}) => {
        details.open = false;
        setExpanded();
        if (restoreFocus) summary.focus();
      };

      options.forEach((option, index) => {
        option.addEventListener('click', () => {
          input.value = option.dataset.value || '';
          value.textContent = option.textContent.trim();
          options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
          close({ restoreFocus: true });
        });

        option.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowDown') nextIndex = (index + 1) % options.length;
          if (event.key === 'ArrowUp') nextIndex = (index - 1 + options.length) % options.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = options.length - 1;
          if (event.key === 'Escape') {
            event.preventDefault();
            close({ restoreFocus: true });
            return;
          }
          if (nextIndex === null) return;
          event.preventDefault();
          options[nextIndex].focus();
        });
      });

      details.addEventListener('toggle', () => {
        setExpanded();
        if (!details.open) return;
        window.requestAnimationFrame(() => {
          const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
          (selected || options[0])?.focus();
        });
      });

      summary.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        details.open = true;
      });

      root.addEventListener('click', (event) => {
        if (dropdown.contains(event.target)) return;
        close();
      });

      setExpanded();
    });
  };

  const setupPriceRanges = (root) => {
    root.querySelectorAll('[data-solvi-price-range]').forEach((range) => {
      if (range.dataset.solviReady === 'true') return;
      range.dataset.solviReady = 'true';

      const minRange = range.querySelector('[data-solvi-price-min-range]');
      const maxRange = range.querySelector('[data-solvi-price-max-range]');
      const group = range.closest('.solvi-filter-group__content');
      const minInput = group?.querySelector('[data-solvi-price-min-input]');
      const maxInput = group?.querySelector('[data-solvi-price-max-input]');
      if (!minRange || !maxRange || !minInput || !maxInput) return;

      const maximum = Number(maxRange.max) || 1;
      const clampValues = (source) => {
        let minimumValue = Number(minRange.value) || 0;
        let maximumValue = Number(maxRange.value) || maximum;

        if (minimumValue > maximumValue) {
          if (source === minRange || source === minInput) maximumValue = minimumValue;
          else minimumValue = maximumValue;
        }

        minRange.value = String(minimumValue);
        maxRange.value = String(maximumValue);
        minInput.value = minimumValue === 0 ? '' : String(minimumValue);
        maxInput.value = maximumValue === maximum ? '' : String(maximumValue);
        range.style.setProperty('--solvi-range-min', `${(minimumValue / maximum) * 100}%`);
        range.style.setProperty('--solvi-range-max', `${(maximumValue / maximum) * 100}%`);
      };

      minRange.addEventListener('input', () => clampValues(minRange));
      maxRange.addEventListener('input', () => clampValues(maxRange));
      minInput.addEventListener('input', () => {
        minRange.value = minInput.value || '0';
        clampValues(minInput);
      });
      maxInput.addEventListener('input', () => {
        maxRange.value = maxInput.value || String(maximum);
        clampValues(maxInput);
      });
      clampValues();
    });
  };

  const setupCollection = (root) => {
    if (!root) return;
    const filterDialog = root.querySelector('[data-solvi-filter-dialog]');
    const quickDialog = root.querySelector('[data-solvi-quick-dialog]');
    const quickContent = quickDialog?.querySelector('[data-solvi-quick-content]');

    setupDialog(filterDialog);
    setupDialog(quickDialog);
    setupFavorites(root);

    root.querySelectorAll('[data-solvi-quick-sniff]').forEach((button) => {
      if (button.dataset.solviReady === 'true') return;
      button.dataset.solviReady = 'true';

      button.addEventListener('click', () => {
        const card = button.closest('[data-solvi-product-card]');
        const template = card?.querySelector('[data-solvi-quick-template]');
        if (!template || !quickContent) return;

        const dialogTitle = quickDialog?.querySelector('[id^="SolviQuickSniffDialogTitle-"]');
        if (dialogTitle) dialogTitle.textContent = button.dataset.solviQuickLabel || 'quick sniff';
        quickContent.replaceChildren(template.content.cloneNode(true));
        openDialog(quickDialog, button);
      });
    });

    root.querySelectorAll('product-recommendations').forEach((recommendations) => {
      if (recommendations.dataset.solviReady === 'true') return;
      recommendations.dataset.solviReady = 'true';
      recommendations.addEventListener('recommendations:loaded', () => setupCollection(root));
    });

    if (root.dataset.solviReady === 'true') return;
    root.dataset.solviReady = 'true';

    setupFamilySelectors(root);
    setupSortDropdowns(root);
    setupPriceRanges(root);

    root.querySelector('[data-solvi-open-filters]')?.addEventListener('click', (event) => {
      openDialog(filterDialog, event.currentTarget);
    });

  };

  const initializeAll = (container = document) => {
    container.querySelectorAll('[data-solvi-collection]').forEach(setupCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', toggleBodyLock);
})();
